<script lang="ts" setup>
import { type VNode, cloneVNode, Comment, Fragment, mergeProps, useSlots } from 'vue';
import { useFormField } from '../hooks/useFormField';
import type { IFormControlProps } from '../types/FormControl';
const props = defineProps<IFormControlProps>();
const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
const slots = useSlots();

const flatChildren = (children?: VNode[]): VNode[] => {
  if (!children) return [];
  return children.flatMap(child => {
    if (child.type === Fragment) return flatChildren(child.children as VNode[]);
    return [child];
  });
};

const CloneDefaultSlot = (attrs: Parameters<typeof mergeProps>[0]) => {
  if (!slots.default) return null;
  const children = flatChildren(slots.default());
  const firstNonCommentChildrenIndex = children.findIndex(child => child.type !== Comment);
  if (firstNonCommentChildrenIndex === -1) return children;
  const firstNonCommentChildren = children[firstNonCommentChildrenIndex];
  delete firstNonCommentChildren.props?.ref;
  const mergedProps = firstNonCommentChildren.props
    ? mergeProps(attrs, firstNonCommentChildren.props)
    : attrs;
  const cloned = cloneVNode({ ...firstNonCommentChildren, props: {} }, mergedProps);
  if (children.length === 1) return cloned;
  children[firstNonCommentChildrenIndex] = cloned;
  return children;
};
</script>

<template>
  <component
    :is="CloneDefaultSlot"
    :id="formItemId"
    data-slot="form-control"
    :aria-describedby="!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`"
    :aria-invalid="!!error"
  ></component>
</template>
